import React, { useState, useRef, useEffect } from 'react';
import { Clock, Webhook as WebhookIcon, CheckCircle, XCircle, PauseCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Webhook } from '../services/api';
import './WebhookCard.css';

interface WebhookCardProps {
  webhook: Webhook;
  isSelected: boolean;
  onClick: () => void;
  isCreating?: boolean;
}

const WebhookCard: React.FC<WebhookCardProps> = ({
  webhook,
  isSelected,
  onClick,
  isCreating = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const eventsListRef = useRef<HTMLDivElement>(null);

  // Check if events list has overflow
  useEffect(() => {
    const checkOverflow = () => {
      if (eventsListRef.current) {
        const hasScroll = eventsListRef.current.scrollHeight > eventsListRef.current.clientHeight;
        setHasOverflow(hasScroll);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [webhook]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getStatusIcon = () => {
    switch (webhook.status) {
      case 'active':
        return <CheckCircle size={14} className="status-icon active" />;
      case 'paused':
        return <PauseCircle size={14} className="status-icon paused" />;
      case 'disabled':
        return <XCircle size={14} className="status-icon disabled" />;
      default:
        return <AlertTriangle size={14} className="status-icon" />;
    }
  };

  const getStatusClass = () => {
    if (webhook.failure_count > 0) return 'has-failures';
    return webhook.status;
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const truncateUrl = (url: string, maxLength: number = 40) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  return (
    <div className="webhook-card-wrapper">
      <div
        className={`webhook-card ${isSelected ? 'selected' : ''} ${isCreating ? 'creating' : ''} ${getStatusClass()} ${isExpanded ? 'expanded' : ''}`}
        onClick={onClick}
      >
        {/* Top color bar */}
        <div className="webhook-card-colorbar" />

        {/* Header */}
        <div className="webhook-card-header">
          <div className="webhook-card-title">
            <span className="webhook-name">{webhook.name || 'New Webhook'}</span>
          </div>
          <div className="webhook-status-badge">
            {getStatusIcon()}
            <span className="status-text">{webhook.status}</span>
          </div>
        </div>

        {/* URL */}
        <div className="webhook-card-url">
          {truncateUrl(webhook.url) || 'No URL specified'}
        </div>

        {/* Events Section */}
        <div className="webhook-card-events-section">
          <div className="events-section-header">
            <div className="events-count">
              <WebhookIcon size={14} />
              <span>{webhook.events?.length || 0} events</span>
            </div>
            {webhook.failure_count > 0 && (
              <div className="failure-badge">
                <AlertTriangle size={12} />
                <span>{webhook.failure_count} failures</span>
              </div>
            )}
          </div>

          {/* Events list with internal scrollbar */}
          <div className="events-list" ref={eventsListRef}>
            {!webhook.events || webhook.events.length === 0 ? (
              <div className="no-events">No events subscribed</div>
            ) : (
              <>
                {webhook.events.map((event, index) => (
                  <div key={`${event}-${index}`} className="event-item">
                    <span className="event-name">{event}</span>
                    <span className={`event-type ${event.split('.')[0]}`}>{event.split('.')[0]}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Show more/less button */}
          {hasOverflow && (
            <button
              className="webhook-card-expand-btn"
              onClick={handleExpandClick}
              aria-label={isExpanded ? "Show less" : "Show more"}
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={14} />
                  <span>Show less</span>
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  <span>Show more</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="webhook-card-footer">
          <div className="webhook-card-meta">
            <Clock size={12} />
            <span>{isCreating ? 'Draft' : formatDate(webhook.created_at)}</span>
          </div>
          <div className="webhook-card-last-success">
            {webhook.last_success_at ? (
              <span className="success-time" title={`Last success: ${new Date(webhook.last_success_at).toLocaleString()}`}>
                {formatRelativeTime(webhook.last_success_at)}
              </span>
            ) : (
              <span className="no-success">No deliveries</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookCard;
